import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";
import { sendEnquiry } from "../lib/api";
import { business } from "../data/mock";

export default function EnquiryForm({ type = "general", extraFields = [], title, subtitle, cta = "Send Message" }) {
  const { toast } = useToast();
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [extra, setExtra] = useState({});

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || (!form.phone && !form.email)) {
      toast({ title: "Almost there", description: "Please add your name and a phone or email." });
      return;
    }
    setSending(true);
    try {
      await sendEnquiry({ ...form, type, details: extra });
      setDone(true);
      toast({ title: "Message sent!", description: "Fred will get back to you — usually the same day." });
      setForm({ name: "", phone: "", email: "", message: "" });
      setExtra({});
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please call Fred on " + business.phone });
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-2xl bg-sky-50 border border-sky-100 p-10 text-center"
      >
        <CheckCircle2 className="mx-auto text-sky-500" size={48} />
        <h3 className="mt-4 font-heading text-xl font-bold text-slate-900">Thanks — message received!</h3>
        <p className="mt-2 text-slate-600">Fred replies personally, usually the same day. In a hurry? Call {business.phone}.</p>
        <button onClick={() => setDone(false)} className="mt-5 text-sky-600 font-semibold hover:underline">Send another</button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 shadow-xl shadow-slate-200/50 p-6 sm:p-8">
      {title && <h3 className="font-heading text-xl font-bold text-slate-900">{title}</h3>}
      {subtitle && <p className="mt-1 text-slate-500 text-sm">{subtitle}</p>}

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="name">Your Name</Label>
          <Input id="name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Smith" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="phone">Phone</Label>
          <Input id="phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="04xx xxx xxx" />
        </div>
      </div>

      <div className="mt-4 space-y-1.5">
        <Label htmlFor="email">Email <span className="text-slate-400 font-normal">(optional)</span></Label>
        <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" />
      </div>

      {extraFields.map((f) => (
        <div key={f.name} className="mt-4 space-y-1.5">
          <Label htmlFor={f.name}>{f.label}</Label>
          {f.type === "select" ? (
            <select
              id={f.name}
              value={extra[f.name] || ""}
              onChange={(e) => setExtra((x) => ({ ...x, [f.name]: e.target.value }))}
              className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              <option value="" disabled>Choose…</option>
              {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <Input id={f.name} value={extra[f.name] || ""} onChange={(e) => setExtra((x) => ({ ...x, [f.name]: e.target.value }))} placeholder={f.placeholder} />
          )}
        </div>
      ))}

      <div className="mt-4 space-y-1.5">
        <Label htmlFor="message">How can Fred help?</Label>
        <Textarea id="message" rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell Fred what you need…" />
      </div>

      <button
        type="submit"
        disabled={sending}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-sky-500 hover:bg-sky-600 disabled:opacity-70 text-white font-semibold px-6 py-3.5 shadow-lg shadow-sky-500/30 transition-all hover:-translate-y-0.5"
      >
        {sending ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <>{cta} <Send size={16} /></>}
      </button>
      <p className="mt-3 text-center text-xs text-slate-400">Prefer to talk? Call {business.phone} — mobile only.</p>
    </form>
  );
}
